<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <b-icon-clock-history class="mr-1"></b-icon-clock-history>
      {{ $t('page.title.player_wallet_history') }}
    </page-header>
    <page-card>
      <template v-slot:header>
        {{ $t('title.filters') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  filter-name="phrase"
                  :label="$t('filters.label.wallet_history.phrase')"
                  :placeholder="$t('filters.placeholder.wallet_history.phrase')">
              </filter-input>
            </div>
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.currency"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="walletCurrency"
                  :label="$t('filters.label.wallet_currency')"
                  :placeholder="$t('filters.placeholder.wallet_currency')">
              </filter-select>
            </div>
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.transactionType"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="operationType"
                  :label="$t('filters.label.operation_type')"
                  :placeholder="$t('filters.placeholder.operation_type')">
              </filter-select>
            </div>
          </div>
            <div class="row">
              <div class="col-4">
                <filter-input-with-operator
                    :filter-query="filterQuery"
                    :operator-options="filterOptions.comparisonOperator"
                    :operator-multiple="false"
                    :default-operator="{value: 'eq', text: 'equals'}"
                    :restrict-input="true"
                    restriction-type="decimals"
                    filter-name="operationAmount"
                    :label="$t('filters.label.operation_amount')"
                >
                </filter-input-with-operator>
              </div>
              <div class="col-4">
                <filter-date-range-picker
                    :filter-query="filterQuery"
                    :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                    :auto="true"
                    :linked="false"
                    :time-picker="true"
                    filter-name="operationCreatedAt"
                    open-direction="left"
                    :label="$t('filters.label.operation_date')">
                </filter-date-range-picker>
              </div>
            </div> <!-- #row-->
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{$t('filters.btn.clear')}}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{$t('filters.btn.apply')}}</b-button>
      </template>
    </page-card>
    <page-card>
      <template v-slot:header>
        {{ $t('title.list') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>

          <div class="table-responsive">
            <b-table striped show-empty
                     :empty-text="$t('table.no_results')"
                     :busy="isBusy"
                     hover
                     head-variant="dark"
                     outlined
                     :sort-by.sync="orderColumn"
                     :sort-desc.sync="orderDirection"
                     :no-local-sorting="true"
                     :items="items"
                     :fields="fields">

              <template v-for='dateSlot in ["operationCreatedAt"]'
                        v-slot:[`cell(${dateSlot})`]="data">
                <template v-if="data.value"> {{ data.value | dateFormat('YYYY-MM-DD HH:mm:ss') }} </template>
              </template>

              <template v-for='idSlot in ["id","walletId"]'
                        v-slot:[`cell(${idSlot})`]="data">
                {{ data.value | shortId }}
              </template>

              <template #cell(operationType)="data">
                <div :id="data.item.id" style="white-space: nowrap">{{ $t(data.value) }} <b-icon-info-circle-fill class="ml-2" v-if="data.item.comment"></b-icon-info-circle-fill> </div>
                <b-tooltip v-if="data.item.comment" :target="data.item.id" triggers="hover">
                  {{data.item.comment}} <br/> created by: {{data.item.createdBy}}
                </b-tooltip>
              </template>

              <template #cell(transactionId)="data">
                <div>
                  <router-link :to="{ name: 'player-transactions', params:{ id: data.item.ownerId },  query: { transactionId: data.value }}">
                    {{ data.value | shortId }}</router-link>
                </div>
              </template>

              <template #cell(ownerNickname)="data">
                <div>
                  <router-link
                      :to="{ name: 'player-details', params: { id: data.item.ownerId }}">
                    {{ data.value }}</router-link>
                </div>
              </template>

              <template #cell(gameId)="data">
                <div>
                  <router-link
                      :to="{ name: 'player-game-history', params: { id: data.item.ownerId }, query: { gameId: data.value }}">
                    {{ data.value | shortId }}</router-link>
                </div>
              </template>

              <template #cell(tournamentId)="data">
                <div>
                  {{ data.value | shortId }}
                </div>
              </template>

              <template v-for='numberSlot in ["operationAmount","walletBalanceAmount","walletLockedFoundsAmount",]'
                        v-slot:[`cell(${numberSlot})`]="data">
                {{ data.value | numberFormat }}
              </template>

              <template v-for='translateSlot in ["walletCurrency",]'
                        v-slot:[`cell(${translateSlot})`]="data">
                 {{  $t(data.value) }}
              </template>

              <template #cell(paymentStatus)="data">
                <b-badge pill variant="warning" v-if="data.value === 'PENDING'">{{$t(data.value)}}</b-badge>
                <b-badge pill variant="danger" v-else-if="data.value === 'REJECTED'">{{$t(data.value)}}</b-badge>
                <b-badge pill variant="success" v-else>{{$t(data.value)}}</b-badge>
              </template>

              <template #cell(transactionType)="data">
                <b-badge pill variant="primary" v-if="data.value === 'PURCHASE'">{{$t(data.value)}}</b-badge>
                <b-badge pill variant="danger" v-else>{{$t(data.value)}}</b-badge>
              </template>

            </b-table>

          </div> <!-- #table-responsive-->

          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>

        </div>
      </template>
    </page-card>
  </div>
</template>

<script>

import axios from "axios";
import FilterDateRangePicker from "../../../components/grid/filters/FilterDateRangePicker";
import FilterInputWithOperator from "../../../components/grid/filters/FilterInputWithOperator";
import FilterSelect from "../../../components/grid/filters/FilterSelect";
import GridMixin from "../../../components/grid/GridMixin";
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";
import PageHeader from "../../../components/page/PageHeader";
import PageCard from "../../../components/page/PageCard";
import FilterInput from "../../../components/grid/filters/FilterInput";

export default {
  name: "WalletHistory",
  mixins: [GridMixin],
  components: {
    PageCard,
    PageHeader,
    FilterInput,
    FilterSelect,
    FilterInputWithOperator,
    FilterDateRangePicker,
    GridPagination
  },
  data() {
    return {
      gridGetUrl: '/api/wallet/history',
      orderColumn: 'operationCreatedAt',
      orderDirection: false,
      fields: [
        {key: 'id', label: this.$t('table.header.operation_id')},
        {key: "gameId", label: this.$t('table.header.game_id')},
        {key: "tournamentId", label: this.$t('table.header.tournament_id')},
        {key: "transactionId", label: this.$t('table.header.transaction_id')},
        {key: 'ownerNickname', label: this.$t('table.header.nick_name')},
        {key: 'ownerEmail', label: this.$t('table.header.email')},
        {key: 'walletCurrency', label: this.$t('table.header.wallet_currency'), sortable: true},
        {key: 'operationType', label: this.$t('table.header.operation_type'), sortable: true},
        {key: 'operationAmount', label: this.$t('table.header.operation_amount'), sortable: true},
        {key: "walletBalanceAmount", label: this.$t('table.header.available_balance_after'), sortable: true},
        {key: "walletLockedFoundsAmount", label: this.$t('table.header.remaining_locked_balance'), sortable: true},
        {key: 'operationCreatedAt', label: this.$t('table.header.operation_date'), sortable: true},
      ],
      filterOptions: {
        comparisonOperator: [
          {value: 'eq', text: 'equals'},
          {value: 'gte', text: 'greater than'},
          {value: 'lte', text: 'lower than'},
          {value: 'btw', text: 'between'},
        ],
        transactionType: [
        ],
        currency: [
          {value: 'FUN_COINS', text: this.$t('FUN_COINS')},
          {value: 'QUIZ_COINS', text: this.$t('QUIZ_COINS')},
        ],
        paymentStatus: [
          {value: 'CONFIRMED', text: this.$t('CONFIRMED')},
          {value: 'REJECTED', text: this.$t('REJECTED')},
          {value: 'PENDING', text: this.$t('PENDING')},
        ],
        paymentMethod: [
          {value: 'MANUAL_PAYMENT_METHOD', text: this.$t('MANUAL_PAYMENT_METHOD')},
          {value: 'BLIK', text: this.$t('BLIK')},
        ],
      },
      items: [],
    };
  },
  mounted() {
    this.getOperationTypes();
    this.$on('refresh-dict', () => {
      this.loadItems();
    })
  },
  methods: {
    getOperationTypes() {
      this.$store.dispatch('root/getWalletDictionary')
          .then(
              data => {
                this.filterOptions.transactionType = data['walletEntryOperationType'].map(d => {
                  return {text: this.$t(d), value: d};
                });
              }
          )
    },
    createFilterQuery() {
      let filter = '';
      Object.keys(this.filterQuery).forEach(key => {
        filter += this.filterQuery[key];

        if (key === 'phrase') {
          filter += '&filters[phrase][0][operator]=like'
        }

        if (key === 'walletCurrency') {
          filter += '&filters[walletCurrency][0][operator]=in'
        }

      })
      return filter;
    },
    loadItems() {
      this.isBusy = true;
      let _that = this;
      let offset = this.perPage * (this.currentPage - 1);
      let fullUrl = this.gridGetUrl + '?limit='
          + this.perPage
          + '&offset=' + offset
          + this.createSortQuery() + this.createFilterQuery();
      axios.get(fullUrl)
          .then(function (response) {
            _that.items = response.data.items;
            _that.totalCount = response.data.totalCount;
            _that.isBusy = false;
          })
          .catch(error => {
            console.log(error);
          });
    },
  },
}
</script>

<style scoped>

</style>
